import PropTypes from 'prop-types';
import {
    Box,
    Stack,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Tooltip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip
} from '@mui/material';
import {
    FilterAltOutlined,
    Search,
    CloseOutlined,
    FileDownloadOutlined,
    RefreshOutlined,
    CalendarMonthOutlined
} from '@mui/icons-material';

import moment from 'moment';

const ALL_MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

const currentYear = moment().year();
const currentMonth = moment().month() + 1;

// Only years up to current year (omitting future years)
const YEAR_OPTIONS = [2024, 2025, currentYear].filter((yr, index, arr) => arr.indexOf(yr) === index && yr <= currentYear);

const TransactionHistoryToolbar = ({
    month,
    year,
    onMonthYearChange,
    usageFromDate,
    usageToDate,
    repaymentFromDate,
    repaymentToDate,
    billPaidStatus,
    onBillPaidStatusChange,
    searchTerm,
    onSearchChange,
    onApplyFilter,
    onResetFilter,
    onExport,
    isExporting = false,
    exportDisabled = false,
    onRefresh,
    loading = false
}) => {
    // Only show current and previous months for current year (hide future months)
    const availableMonths = ALL_MONTHS.filter((m) => {
        if (Number(year) >= currentYear) {
            return m.value <= currentMonth;
        }
        return true;
    });

    const handleYearChange = (newYear) => {
        let nextMonth = Number(month);
        if (Number(newYear) >= currentYear && nextMonth > currentMonth) {
            nextMonth = currentMonth;
        }
        onMonthYearChange(nextMonth, Number(newYear));
    };

    return (
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
            {/* Primary Controls Row */}
            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', lg: 'center' }}
                justifyContent="space-between"
            >
                {/* 1-Click Monthly Cycle & Bill Status Filters */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                    {/* Month Selector */}
                    <FormControl size="small" sx={{ minWidth: 135, bgcolor: '#f8fafc', borderRadius: 2 }}>
                        <InputLabel id="history-month-label">Month</InputLabel>
                        <Select
                            labelId="history-month-label"
                            value={Number(month)}
                            label="Month"
                            onChange={(e) => onMonthYearChange(Number(e.target.value), Number(year))}
                        >
                            {availableMonths.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Year Selector */}
                    <FormControl size="small" sx={{ minWidth: 105, bgcolor: '#f8fafc', borderRadius: 2 }}>
                        <InputLabel id="history-year-label">Year</InputLabel>
                        <Select
                            labelId="history-year-label"
                            value={Number(year)}
                            label="Year"
                            onChange={(e) => handleYearChange(Number(e.target.value))}
                        >
                            {YEAR_OPTIONS.map((yr) => (
                                <MenuItem key={yr} value={yr}>
                                    {yr}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Bill Paid Status Filter */}
                    <FormControl size="small" sx={{ minWidth: 140, bgcolor: '#f8fafc', borderRadius: 2 }}>
                        <InputLabel id="history-status-label">Bill Status</InputLabel>
                        <Select
                            labelId="history-status-label"
                            value={billPaidStatus}
                            label="Bill Status"
                            onChange={(e) => onBillPaidStatusChange(e.target.value)}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="PAID">Paid</MenuItem>
                            <MenuItem value="NOT PAID">Not Paid</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Apply & Reset Buttons */}
                    <Button
                        variant="contained"
                        startIcon={<FilterAltOutlined />}
                        onClick={onApplyFilter}
                        disabled={loading}
                        sx={{
                            bgcolor: '#1e40af',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 2.2,
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        Apply
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onResetFilter}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': { bgcolor: '#f8fafc' }
                        }}
                    >
                        Reset
                    </Button>
                </Stack>

                {/* Search & Export / Refresh Actions */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <TextField
                        placeholder="Search name, mobile, email, RRN, ID..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        sx={{
                            width: { xs: '100%', sm: 290 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: '#f8fafc'
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => onSearchChange('')}>
                                        <CloseOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />

                    <Tooltip title="Export to Excel">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={
                                    isExporting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <FileDownloadOutlined sx={{ fontSize: '18px !important' }} />
                                    )
                                }
                                onClick={onExport}
                                disabled={isExporting || exportDisabled}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    bgcolor: '#fff',
                                    '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                                }}
                            >
                                {isExporting ? 'Exporting...' : 'Export'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Refresh Repayment History">
                        <span>
                            <IconButton
                                size="small"
                                onClick={onRefresh}
                                disabled={loading}
                                sx={{
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: '#fff',
                                    '&:hover': { bgcolor: '#f8fafc' }
                                }}
                            >
                                <RefreshOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Cycle Date Preview Badge */}
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                    icon={<CalendarMonthOutlined sx={{ fontSize: '15px !important' }} />}
                    label={
                        <span>
                            Usage Cycle: <strong>{usageFromDate}</strong> to <strong>{usageToDate}</strong> | Repayment:{' '}
                            <strong>{repaymentFromDate}</strong> to <strong>{repaymentToDate}</strong>
                        </span>
                    }
                    size="small"
                    sx={{
                        bgcolor: '#f1f5f9',
                        color: '#334155',
                        borderRadius: 1.5,
                        fontSize: '0.75rem',
                        height: 24,
                        '& .MuiChip-label': { px: 1 }
                    }}
                />
            </Box>
        </Box>
    );
};

TransactionHistoryToolbar.propTypes = {
    month: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onMonthYearChange: PropTypes.func.isRequired,
    usageFromDate: PropTypes.string.isRequired,
    usageToDate: PropTypes.string.isRequired,
    repaymentFromDate: PropTypes.string.isRequired,
    repaymentToDate: PropTypes.string.isRequired,
    billPaidStatus: PropTypes.string.isRequired,
    onBillPaidStatusChange: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    onApplyFilter: PropTypes.func.isRequired,
    onResetFilter: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
    isExporting: PropTypes.bool,
    exportDisabled: PropTypes.bool,
    onRefresh: PropTypes.func.isRequired,
    loading: PropTypes.bool
};

export default TransactionHistoryToolbar;
